import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { Copy, Check } from "../shared/Icon";
import { cn } from "../../lib/cn";

interface Props {
  content: string;
  /** When true, render as plain text (used by user bubbles for safety). */
  plain?: boolean;
}

/**
 * Themed markdown renderer.
 * Inline code → mono chip on bg-inset; fences → bordered block with copy
 * button and language label; links open in a new tab.
 */
export function MessageContent({ content, plain }: Props) {
  if (plain) {
    return <span className="whitespace-pre-wrap text-body">{content}</span>;
  }
  return (
    <div className={cn("text-body text-text-primary space-y-2")}>
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="leading-relaxed">{children}</p>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-accent underline-offset-2 hover:underline"
            >
              {children}
            </a>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-outside ml-5 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside ml-5 space-y-1">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-text-primary">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="text-text-secondary italic">{children}</em>
          ),
          code: ({ className, children, ...props }: CodeProps) => {
            // react-markdown v9 dropped the legacy `inline` prop. A fenced
            // block carries a `language-*` class and (almost always) at least
            // one newline; bare backticks have neither. Without this check
            // every `foo` inline gets boxed as a "TEXT" copy card.
            const childStr = String(children ?? "");
            const isFence =
              (className || "").includes("language-") || childStr.includes("\n");
            if (!isFence) {
              return (
                <code
                  className={cn(
                    "font-mono text-[12.5px] px-1 py-[1px] rounded-sm",
                    "bg-bg-inset text-text-primary",
                  )}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            const lang =
              (className || "").replace("language-", "").trim() || "text";
            return <CodeBlock language={lang}>{childStr}</CodeBlock>;
          },
          pre: ({ children }) => <>{children}</>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

type CodeProps = {
  className?: string;
  children?: ReactNode;
};

function CodeBlock({
  language,
  children,
}: {
  language: string;
  children: string;
}) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    void navigator.clipboard.writeText(children).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <div className="rounded-md border border-border-subtle bg-bg-inset overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1 border-b border-border-subtle">
        <span className="text-caption text-text-tertiary">{language}</span>
        <button
          onClick={onCopy}
          aria-label="Copy code"
          className={cn(
            "inline-flex items-center gap-1 h-6 px-1.5 rounded-sm",
            "text-text-tertiary hover:text-text-primary hover:bg-bg-surface",
            "transition-colors duration-fast ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
          )}
        >
          {copied ? <Check /> : <Copy />}
          <span className="text-mono text-[11px]">
            {copied ? "copied" : "copy"}
          </span>
        </button>
      </div>
      <pre className="overflow-x-auto p-3 font-mono text-[12.5px] leading-relaxed text-text-primary">
        {children.replace(/\n$/, "")}
      </pre>
    </div>
  );
}
